import { useEffect, useRef, useState } from "react";
import { api, fmtEUR, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Camera, X, Plus, Minus, Trash2, ScanLine, CreditCard, Banknote, Printer, Percent, Package, Search, MapPin, QrCode } from "lucide-react";
import PosInvoicePrint from "./PosInvoicePrint";

export default function Pos() {
  const [items, setItems] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanMode, setScanMode] = useState("product"); // "product" or "client"
  const [showStock, setShowStock] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [showCash, setShowCash] = useState(false);
  const [cashGiven, setCashGiven] = useState("");
  const [saving, setSaving] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loyaltyClient, setLoyaltyClient] = useState(null);
  const [applyBonus, setApplyBonus] = useState(false);
  const scannerRef = useRef(null);
  const beepRef = useRef(null);

  useEffect(() => { loadRecent(); }, []);
  const loadRecent = () => api.get("/pos/my-invoices?limit=5").then(r => setRecentInvoices(r.data)).catch(() => {});

  const playBeep = () => {
    try {
      const ctx = beepRef.current || new (window.AudioContext || window.webkitAudioContext)();
      beepRef.current = ctx;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = 1200;
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.35, ctx.currentTime + 0.01);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.18);
      osc.start(); osc.stop(ctx.currentTime + 0.2);
    } catch {}
    try {
      if (navigator.vibrate) navigator.vibrate([80, 40, 60]);
    } catch {}
  };

  const gross = items.reduce((s, i) => s + i.unit_price * i.qty, 0);
  const subtotal = items.reduce((s, i) => s + (i.unit_price * i.qty) / (1 + i.vat_rate / 100), 0);
  const vatTotal = gross - subtotal;
  const discountPct = Math.max(0, Math.min(100, parseFloat(discount) || 0));
  const discountAmt = gross * discountPct / 100;
  const total = gross - discountAmt;

  const addProduct = (p) => {
    setItems(prev => {
      const idx = prev.findIndex(x => x.product_id === p.id);
      if (idx >= 0) {
        if (prev[idx].qty >= p.stock_qty) { toast.error(`Maksimumi ${p.stock_qty} ${p.unit || "copë"}.`); return prev; }
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + 1 };
        return copy;
      }
      return [...prev, {
        product_id: p.id, sku: p.sku || "", article_number: p.article_number || "",
        name: p.name, image: (p.images && p.images[0]) || "", unit: p.unit || "copë",
        unit_price: p.discount_price ?? p.price, qty: 1, vat_rate: p.vat_rate ?? 18,
        stock_qty: p.stock_qty ?? 0,
      }];
    });
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch {}
      scannerRef.current = null;
    }
  };
  const startScanner = async () => {
    setScanning(true);
    setTimeout(async () => {
      const mod = await import("html5-qrcode");
      const { Html5Qrcode, Html5QrcodeSupportedFormats } = mod;
      const s = new Html5Qrcode("pos-qr-reader", {
        verbose: false,
        formatsToSupport: [
          Html5QrcodeSupportedFormats.QR_CODE, Html5QrcodeSupportedFormats.EAN_13, Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A, Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.CODE_93, Html5QrcodeSupportedFormats.ITF,
        ],
      });
      scannerRef.current = s;
      let handling = false;
      try {
        await s.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 280, height: 200 } },
          async (decoded) => {
            if (handling) return;
            handling = true;
            try { s.pause(true); } catch {}
            // Flash green overlay
            const el = document.getElementById("scan-flash");
            if (el) { el.style.background = "rgba(16,185,129,0.55)"; setTimeout(() => { if (el) el.style.background = "transparent"; }, 350); }
            playBeep();
            try {
              if (scanMode === "client") {
                const r = await api.get(`/pos/loyalty/lookup?token=${encodeURIComponent(decoded)}`);
                setLoyaltyClient(r.data);
                setApplyBonus(!!r.data.active_tier);
                toast.success(`Klient: ${r.data.name} ${r.data.client_number}`);
                setTimeout(async () => { await closeScan(); }, 500);
              } else {
                const r = await api.get(`/pos/lookup?code=${encodeURIComponent(decoded)}`);
                addProduct(r.data);
                toast.success(`+ ${r.data.name}`);
                setTimeout(async () => { await closeScan(); }, 500);
              }
            } catch (e) {
              toast.error(e.response?.data?.detail || (scanMode === "client" ? "Klienti nuk u gjet" : "Produkti nuk u gjet"));
              setTimeout(() => { handling = false; try { s.resume(); } catch {} }, 1200);
            }
          }, () => {}
        );
      } catch (e) { toast.error("Nuk mund të hapet kamera"); setScanning(false); }
    }, 100);
  };
  const closeScan = async () => { await stopScanner(); setScanning(false); };

  const lookup = async (code) => {
    if (!code) return;
    try {
      const r = await api.get(`/pos/lookup?code=${encodeURIComponent(code)}`);
      addProduct(r.data);
      toast.success(`+ ${r.data.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Produkti nuk u gjet"); }
  };

  const setQty = (idx, qty) => setItems(prev => {
    const copy = [...prev]; const it = copy[idx];
    const newQty = Math.max(1, qty);
    if (newQty > it.stock_qty) { toast.error(`Maks ${it.stock_qty} ${it.unit}`); return prev; }
    copy[idx] = { ...it, qty: newQty }; return copy;
  });
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const submitInvoice = async (paymentMethod, extra = {}) => {
    if (items.length === 0) { toast.error("Shporta është e zbrazët"); return; }
    setSaving(true);
    try {
      const payload = {
        items: items.map(i => ({
          product_id: i.product_id, sku: i.sku, article_number: i.article_number,
          name: i.name, image: i.image, unit: i.unit, unit_price: i.unit_price,
          qty: i.qty, vat_rate: i.vat_rate,
        })),
        payment_method: paymentMethod,
        discount_percent: discountPct,
        client_id: loyaltyClient?.id || null,
        apply_bonus_tier_id: (applyBonus && loyaltyClient?.active_tier?.id) || null,
        ...extra,
      };
      const r = await api.post("/pos/invoice", payload);
      toast.success(`Fatura ${r.data.invoice_number} u ruajt!`);
      setLastInvoice(r.data);
      setItems([]); setShowCash(false); setCashGiven(""); setDiscount(0);
      setLoyaltyClient(null); setApplyBonus(false);
      loadRecent();
    } catch (e) { toast.error(e.response?.data?.detail || "Gabim gjatë ruajtjes"); }
    setSaving(false);
  };

  const cashChange = cashGiven && !isNaN(parseFloat(cashGiven)) ? Math.max(0, parseFloat(cashGiven) - total) : 0;

  return (
    <div className="p-3 sm:p-4 lg:p-6 pb-40 lg:pb-6">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h1 className="font-display text-xl sm:text-3xl uppercase">Arka</h1>
        <div className="flex gap-2 lg:hidden">
          <button onClick={() => setShowStock(true)} data-testid="pos-stock-btn-top"
            className="bg-[#FFD600] hover:bg-[#e6c000] text-black font-bold uppercase text-xs px-3 py-2 flex items-center gap-1.5">
            <Package size={14} /> Stoku
          </button>
          <button onClick={() => { setScanMode("product"); startScanner(); }} data-testid="pos-scan-btn-top"
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold uppercase text-xs px-3 py-2 flex items-center gap-1.5">
            <Camera size={14} /> Skano
          </button>
          <button onClick={() => { setScanMode("client"); startScanner(); }} data-testid="pos-scan-client-top"
            className="bg-[#FFD600] hover:bg-yellow-300 text-black font-bold uppercase text-xs px-3 py-2 flex items-center gap-1.5">
            <QrCode size={14} /> QR
          </button>
        </div>
      </div>

      {loyaltyClient && (
        <div className="hg-card p-3 mb-3 bg-[#FFD600]/10 border-[#FFD600]/60" data-testid="pos-loyalty-bar">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#FFD600] font-bold">Klienti</div>
              <div className="font-bold truncate">{loyaltyClient.name} <span className="font-mono text-xs text-neutral-400 ml-1">{loyaltyClient.client_number}</span></div>
              <div className="text-[11px] text-neutral-400">Blerje totale: <span className="text-emerald-400 font-bold">{fmtEUR(loyaltyClient.lifetime_spend)}</span></div>
              {loyaltyClient.active_tier && (
                <label className="mt-2 flex items-center gap-2 cursor-pointer bg-emerald-500/10 p-2 border border-emerald-500/40">
                  <input type="checkbox" checked={applyBonus} onChange={e => setApplyBonus(e.target.checked)}
                    data-testid="pos-apply-bonus" className="w-4 h-4 accent-emerald-500" />
                  <span className="text-xs font-bold text-emerald-400">Apliko bonusin: {loyaltyClient.active_tier.discount_pct}% zbritje</span>
                </label>
              )}
              {!loyaltyClient.active_tier && loyaltyClient.next_tier && (
                <div className="text-[10px] text-neutral-500 mt-1">Deri në bonusin tjetër: {fmtEUR(loyaltyClient.amount_to_next)} → {loyaltyClient.next_tier.discount_pct}%</div>
              )}
            </div>
            <button onClick={() => { setLoyaltyClient(null); setApplyBonus(false); }} data-testid="pos-loyalty-clear"
              className="p-1 text-neutral-400 hover:text-red-400"><X size={16} /></button>
          </div>
        </div>
      )}

      {/* Manual code (large, prominent on mobile) */}
      <form onSubmit={(e) => { e.preventDefault(); lookup(manualCode); setManualCode(""); }} className="flex gap-2 mb-3">
        <input value={manualCode} onChange={e => setManualCode(e.target.value)}
          placeholder="Shkruaj/skano artikullin (HG-...)" autoFocus
          data-testid="pos-manual-code"
          className="flex-1 bg-black border-2 border-[#262626] px-3 py-3 text-base sm:text-sm focus:border-[#FFD600] outline-none" />
        <button type="submit" data-testid="pos-manual-add"
          className="hg-btn-primary px-4 uppercase text-xs font-bold flex items-center gap-1"><Plus size={16} /></button>
      </form>

      <div className="grid lg:grid-cols-[1fr_340px] gap-3">
        {/* CART */}
        <div className="hg-card p-3 sm:p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-bold text-base sm:text-lg">Shporta ({items.length})</div>
            {items.length > 0 && (
              <button onClick={() => setItems([])} data-testid="pos-cart-clear"
                className="text-xs uppercase text-red-400 flex items-center gap-1"><Trash2 size={12} /> Pastro</button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="text-center py-10 text-neutral-500">
              <ScanLine size={40} className="mx-auto mb-2 opacity-50" />
              <div className="text-xs sm:text-sm">Skano ose shkruaj kodin e produktit</div>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="border border-[#262626] p-2 sm:p-2.5" data-testid={`pos-cart-item-${idx}`}>
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white flex-shrink-0 overflow-hidden">
                      {it.image && <img src={mediaUrl(it.image)} alt={it.name} className="w-full h-full object-contain" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-xs sm:text-sm leading-tight line-clamp-2">{it.name}</div>
                      <div className="text-[10px] sm:text-xs text-neutral-500 font-mono truncate mt-0.5">{it.article_number || it.sku}</div>
                      <div className="text-[10px] sm:text-xs text-neutral-400 mt-0.5">{fmtEUR(it.unit_price)} × {it.qty} = <span className="font-bold text-white">{fmtEUR(it.unit_price * it.qty)}</span></div>
                    </div>
                    <button onClick={() => removeItem(idx)} data-testid={`pos-remove-${idx}`}
                      className="w-8 h-8 text-red-400 hover:bg-red-500/10 flex items-center justify-center flex-shrink-0"><X size={16} /></button>
                  </div>
                  <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-[#262626]/50">
                    <button onClick={() => setQty(idx, it.qty - 1)} className="w-9 h-9 border border-[#262626] flex items-center justify-center active:bg-[#FFD600] active:text-black"><Minus size={14} /></button>
                    <input value={it.qty} onChange={e => setQty(idx, parseInt(e.target.value) || 1)} data-testid={`pos-qty-${idx}`}
                      className="w-14 h-9 text-center bg-black border border-[#262626] text-sm font-bold" />
                    <button onClick={() => setQty(idx, it.qty + 1)} className="w-9 h-9 border border-[#262626] flex items-center justify-center active:bg-[#FFD600] active:text-black"><Plus size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {items.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#262626]">
              {/* Discount input */}
              <div className="flex items-center gap-2 mb-3 bg-black/40 p-2 border border-[#262626]">
                <Percent size={14} className="text-[#FFD600]" />
                <label className="text-xs uppercase text-neutral-400">Zbritje</label>
                <input type="number" min="0" max="100" step="1" value={discount || ""}
                  onChange={e => setDiscount(e.target.value)} data-testid="pos-discount"
                  placeholder="0"
                  className="w-16 h-8 bg-black border border-[#262626] px-2 text-center focus:border-[#FFD600] outline-none" />
                <span className="text-xs text-neutral-400">%</span>
                {discountAmt > 0 && <span className="ml-auto text-xs text-emerald-400">−{fmtEUR(discountAmt)}</span>}
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-neutral-400">Nëntotali</span><span>{fmtEUR(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-400">TVSH</span><span>{fmtEUR(vatTotal)}</span></div>
                {discountAmt > 0 && <div className="flex justify-between text-emerald-400"><span>Zbritje ({discountPct}%)</span><span>−{fmtEUR(discountAmt)}</span></div>}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-[#262626]">
                  <span>TOTALI</span>
                  <span className="text-[#FFD600]" data-testid="pos-total">{fmtEUR(total)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT PANEL (desktop) */}
        <div className="hidden lg:block space-y-3">
          <div className="hg-card p-4 text-center bg-emerald-500/5 border-emerald-500/40">
            <div className="text-xs uppercase tracking-widest text-emerald-400 font-bold mb-2">Skano produkt</div>
            <button onClick={() => { setScanMode("product"); startScanner(); }} data-testid="pos-scan-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold uppercase text-sm py-3 flex items-center justify-center gap-2">
              <Camera size={16} /> Hap kamerën
            </button>
          </div>
          <div className="hg-card p-4 text-center bg-[#FFD600]/5 border-[#FFD600]/40">
            <div className="text-xs uppercase tracking-widest text-[#FFD600] font-bold mb-2">QR Klienti</div>
            <button onClick={() => { setScanMode("client"); startScanner(); }} data-testid="pos-scan-client-btn"
              className="w-full bg-[#FFD600] hover:bg-yellow-300 text-black font-bold uppercase text-sm py-3 flex items-center justify-center gap-2">
              <QrCode size={16} /> Skano QR
            </button>
          </div>
          <button onClick={() => setShowStock(true)} data-testid="pos-stock-btn"
            className="w-full hg-card p-4 hover:border-[#FFD600] transition-colors flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FFD600]/10 text-[#FFD600] flex items-center justify-center flex-shrink-0"><Package size={20} /></div>
            <div className="text-left flex-1">
              <div className="text-xs uppercase tracking-widest text-[#FFD600] font-bold">Shiko stokun</div>
              <div className="text-xs text-neutral-400">Kërko artikullin (vetëm shikim)</div>
            </div>
          </button>
          <div className="hg-card p-4">
            <div className="text-xs uppercase text-neutral-400 text-center mb-1">Totali</div>
            <div className="text-4xl font-bold text-center mb-4" data-testid="pos-total-big">{fmtEUR(total)}</div>
            <button onClick={() => setShowCash(true)} disabled={items.length === 0} data-testid="pos-pay-cash"
              className="w-full py-3.5 mb-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-30 text-black font-bold uppercase text-sm flex items-center justify-center gap-2">
              <Banknote size={18} /> Kesh
            </button>
            <button onClick={() => submitInvoice("card")} disabled={items.length === 0 || saving} data-testid="pos-pay-card"
              className="w-full py-3.5 bg-blue-500 hover:bg-blue-400 disabled:opacity-30 text-white font-bold uppercase text-sm flex items-center justify-center gap-2">
              <CreditCard size={18} /> Kartë
            </button>
          </div>
        </div>
      </div>

      {/* Recent invoices */}
      <div className="hg-card p-3 sm:p-4 mt-3">
        <div className="font-bold text-sm sm:text-base mb-2">Faturat e fundit</div>
        {recentInvoices.length === 0 ? (
          <div className="text-xs text-neutral-500 text-center py-3">Ende s'ka fatura</div>
        ) : (
          <div className="space-y-1.5">
            {recentInvoices.map(inv => (
              <div key={inv.id} onClick={() => setLastInvoice(inv)} data-testid={`pos-recent-${inv.invoice_number}`}
                className="flex items-center gap-2 border border-[#262626] p-2 cursor-pointer hover:border-[#FFD600]">
                <div className="font-mono text-xs font-bold flex-shrink-0">{inv.invoice_number}</div>
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${
                  inv.payment_method === "cash" ? "bg-emerald-500/15 text-emerald-400"
                  : inv.payment_method === "card" ? "bg-blue-500/15 text-blue-400"
                  : "bg-[#FFD600]/15 text-[#FFD600]"
                }`}>{inv.payment_method === "cash" ? "Kesh" : inv.payment_method === "card" ? "Kartë" : "Fat."}</span>
                <div className="text-[10px] text-neutral-400 flex-1 min-w-0 truncate">{new Date(inv.created_at).toLocaleTimeString("sq")}</div>
                <div className="font-bold text-sm flex-shrink-0">{fmtEUR(inv.total)}</div>
                <Printer size={12} className="text-neutral-500 flex-shrink-0" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mobile sticky payment bar */}
      {items.length > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#0f0f0f] border-t-2 border-[#FFD600] p-3 z-40">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase text-neutral-400">Totali</div>
            <div className="text-2xl font-bold text-[#FFD600]" data-testid="pos-total-mobile">{fmtEUR(total)}</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setShowCash(true)} data-testid="pos-pay-cash-mobile"
              className="py-3 bg-emerald-500 hover:bg-emerald-400 text-black font-bold uppercase text-xs flex items-center justify-center gap-1.5">
              <Banknote size={14} /> Kesh
            </button>
            <button onClick={() => submitInvoice("card")} disabled={saving} data-testid="pos-pay-card-mobile"
              className="py-3 bg-blue-500 hover:bg-blue-400 disabled:opacity-30 text-white font-bold uppercase text-xs flex items-center justify-center gap-1.5">
              <CreditCard size={14} /> Kartë
            </button>
          </div>
        </div>
      )}

      {/* Scanner modal */}
      {scanning && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          <div className="flex items-center justify-between p-3 border-b border-[#262626]">
            <div className="text-sm font-bold uppercase tracking-widest">Skano produktin</div>
            <button onClick={closeScan} data-testid="pos-scan-close" className="p-2 hover:bg-[#262626]"><X size={20} /></button>
          </div>
          <div className="relative flex-1 min-h-0">
            <div id="pos-qr-reader" className="absolute inset-0 flex items-center justify-center bg-black" />
            <div id="scan-flash" className="pointer-events-none absolute inset-0 transition-colors" style={{ background: "transparent" }} />
          </div>
          <div className="text-center text-xs text-neutral-500 py-2">Drejtoje kamerën te barcode-i • auto-mbyllet pas skanimit</div>
        </div>
      )}

      {/* Cash modal */}
      {showCash && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowCash(false)}>
          <div className="hg-card max-w-sm w-full p-5" onClick={e => e.stopPropagation()}>
            <div className="font-display text-2xl uppercase mb-3">Pagesë me Kesh</div>
            <div className="text-sm text-neutral-400 mb-1">Totali për pagesë</div>
            <div className="text-3xl font-bold mb-4">{fmtEUR(total)}</div>
            <label className="text-xs uppercase text-neutral-400 mb-1 block">Shuma e dhënë</label>
            <input value={cashGiven} onChange={e => setCashGiven(e.target.value)} type="number" step="0.01" autoFocus inputMode="decimal"
              data-testid="pos-cash-given"
              className="w-full bg-black border border-[#262626] px-4 py-3 text-2xl font-bold text-center focus:border-[#FFD600] outline-none mb-3" />
            {cashGiven && !isNaN(parseFloat(cashGiven)) && (
              <div className={`p-3 border-2 mb-3 ${parseFloat(cashGiven) >= total ? "border-emerald-500 bg-emerald-500/10" : "border-red-500 bg-red-500/10"}`}>
                <div className="text-xs uppercase">Kusuri</div>
                <div className="text-2xl font-bold" data-testid="pos-cash-change">
                  {parseFloat(cashGiven) >= total ? fmtEUR(cashChange) : "Pamjaftueshëm"}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setShowCash(false)} className="flex-1 py-3 border border-[#262626] uppercase text-xs">Anulo</button>
              <button onClick={() => submitInvoice("cash", { cash_given: parseFloat(cashGiven) })}
                disabled={!cashGiven || parseFloat(cashGiven) < total || saving}
                data-testid="pos-cash-confirm"
                className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-30 text-black font-bold uppercase text-xs">
                {saving ? "..." : "Përfundo"}
              </button>
            </div>
          </div>
        </div>
      )}

      {lastInvoice && <PosInvoicePrint invoice={lastInvoice} onClose={() => setLastInvoice(null)} />}
      {showStock && <StockBrowser onClose={() => setShowStock(false)} onPick={(p) => { addProduct(p); setShowStock(false); }} />}
    </div>
  );
}

function StockBrowser({ onClose, onPick }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [locMap, setLocMap] = useState({}); // productId -> [locations]

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const url = q.trim() ? `/products?q=${encodeURIComponent(q.trim())}&limit=50` : "/products?limit=50";
        const r = await api.get(url);
        setProducts(r.data);
      } catch { setProducts([]); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const loadLocations = async (pid) => {
    if (locMap[pid]) return;
    try {
      const r = await api.get(`/pos/lookup?code=${encodeURIComponent(pid)}`);
      setLocMap(prev => ({ ...prev, [pid]: r.data.locations || [] }));
    } catch { setLocMap(prev => ({ ...prev, [pid]: [] })); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" onClick={onClose}>
      <div className="flex items-center gap-2 p-3 border-b border-[#262626]" onClick={e => e.stopPropagation()}>
        <Package size={20} className="text-[#FFD600]" />
        <div className="font-bold uppercase tracking-widest text-sm">Stoku — Ku ndodhet produkti</div>
        <button onClick={onClose} data-testid="stock-close" className="ml-auto p-2 hover:bg-[#262626]"><X size={20} /></button>
      </div>
      <div className="p-3 border-b border-[#262626]" onClick={e => e.stopPropagation()}>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input value={q} onChange={e => setQ(e.target.value)} autoFocus
            placeholder="Kërko sipas emrit, artikullit ose SKU..."
            data-testid="stock-search"
            className="w-full bg-black border-2 border-[#262626] pl-10 pr-3 py-3 focus:border-[#FFD600] outline-none" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3" onClick={e => e.stopPropagation()}>
        {loading ? (
          <div className="text-center py-6 text-neutral-400 text-sm">Duke ngarkuar...</div>
        ) : products.length === 0 ? (
          <div className="text-center py-10 text-neutral-500 text-sm">Nuk u gjet asnjë produkt</div>
        ) : (
          <div className="space-y-2">
            {products.map(p => {
              const stockOk = (p.stock_qty || 0) > 0;
              const locs = locMap[p.id];
              return (
                <div key={p.id} className={`border border-[#262626] p-2.5 ${!stockOk ? "opacity-60" : ""}`} data-testid={`stock-row-${p.id}`}>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-12 h-12 bg-white flex-shrink-0 overflow-hidden">
                      {p.images?.[0] && <img src={mediaUrl(p.images[0])} alt={p.name} className="w-full h-full object-contain" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-xs sm:text-sm truncate">{p.name}</div>
                      <div className="text-[10px] sm:text-xs text-neutral-500 font-mono truncate">{p.article_number || p.sku}</div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-wider ${stockOk ? "text-emerald-400" : "text-red-400"}`}>
                          {stockOk ? `${p.stock_qty} ${p.unit || "copë"}` : "Jashtë stokut"}
                        </span>
                        <span className="text-xs font-bold text-[#FFD600]">{fmtEUR(p.discount_price ?? p.price)}</span>
                      </div>
                    </div>
                    {stockOk && (
                      <button onClick={() => onPick(p)} data-testid={`stock-pick-${p.id}`}
                        className="w-9 h-9 bg-[#FFD600] hover:bg-[#e6c000] text-black flex items-center justify-center flex-shrink-0">
                        <Plus size={16} />
                      </button>
                    )}
                  </div>
                  {/* Locations */}
                  <div className="mt-2 pt-2 border-t border-[#262626]/60">
                    {!locs ? (
                      <button onClick={() => loadLocations(p.id)} data-testid={`stock-show-loc-${p.id}`}
                        className="text-[10px] uppercase tracking-widest text-[#FFD600] hover:underline flex items-center gap-1">
                        <MapPin size={11} /> Shiko ku ndodhet
                      </button>
                    ) : locs.length === 0 ? (
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Pa lokacion të regjistruar</div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {locs.map((l, i) => (
                          <div key={i} className="flex items-center gap-1 bg-[#FFD600]/10 border border-[#FFD600]/40 px-2 py-0.5 text-[10px] font-mono">
                            <MapPin size={10} className="text-[#FFD600]" />
                            <span className="font-bold text-[#FFD600]">{l.cabinet}-{l.drawer}-{l.position}</span>
                            <span className="text-neutral-400">·</span>
                            <span className="text-emerald-400 font-bold">{l.qty}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
